"use client";

import React, { useEffect, useRef, useState } from "react";
import { Shield, Mail, MapPin, CheckCircle2, ArrowRight, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const userRights = [{
  title: "Access to Portfolio Data",
  description: "You have the right to request comprehensive reports of the personal and financial data we maintain within your account."
}, {
  title: "Right to Rectification",
  description: "You have the right to update or correct any investment profile information that you believe is inaccurate or outdated."
}, {
  title: "Right to Deletion",
  description: "You have the right to request the removal of your data from our active systems, subject to financial regulatory retention requirements."
}, {
  title: "Data Usage Control",
  description: "You have the right to request that we limit the processing of your data for specific analytical or secondary purposes."
}, {
  title: "Marketing Preferences",
  description: "You have the right to object to our use of your data for direct marketing and to opt-out of promotional communications at any time."
}, {
  title: "Secure Data Portability",
  description: "You have the right to request a secure transfer of your investment data to another institution or directly to yourself."
}];
const contactMethods = [{
  icon: Mail,
  title: "Compliance Email",
  detail: "compliance@investment-firm.com",
  description: "For formal data subject requests and regulatory inquiries."
}, {
  icon: MapPin,
  title: "Corporate Headquarters",
  detail: "Chief Privacy Officer",
  description: "Financial Plaza, 100 Wall Street, New York, NY 10005"
}];
export default function UserRightsContactInformation() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="165217" id="your-rights-contact-information" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={sectionRef} className={`grid gap-12 lg:grid-cols-2 lg:gap-16 items-start transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Content Column */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                <Shield className="mr-2 h-4 w-4" />
                Investor Safety
              </div>
              <h2 className="text-3xl font-default font-bold tracking-tight sm:text-4xl md:text-5xl text-foreground">
                Your Investor Data & Privacy Rights
              </h2>
              <p className="text-lg font-sans text-muted-foreground max-w-[600px]">
                We prioritize transparency and security. As an investor, you maintain full control over the personal and financial information you entrust to our management.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              {userRights.map((right, i) => <div key={i} data-index={i} className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-foreground font-default font-semibold">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                    <h3 className="leading-tight">{right.title}</h3>
                  </div>
                  <p className="text-sm font-sans text-muted-foreground pl-7">
                    {right.description}
                  </p>
                </div>)}
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-sm font-sans text-muted-foreground mb-4">
                For a detailed explanation of our data governance framework, please review our full disclosure documentation.
              </p>
              <Button variant="outline" asChild className="font-sans">
                <Link to="#">
                  <FileText className="mr-2 h-4 w-4" />
                  View Disclosure Policy
                </Link>
              </Button>
            </div>
          </div>

          {/* Contact Card Column */}
          <div className="lg:pl-8">
            <Card className="border-border/50 shadow-xl bg-card/50 backdrop-blur-sm rounded-xl">
              <CardHeader className="space-y-2">
                <CardTitle className="text-2xl font-default">Contact Our Compliance Office</CardTitle>
                <CardDescription className="text-base font-sans">
                  Our dedicated compliance team is available to address any inquiries regarding your data security, regulatory rights, or privacy practices.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-6 pt-4">
                {contactMethods.map((method, i) => <div key={i} data-index={i} className="flex items-start gap-4 p-4 rounded-lg bg-background/50 border border-border/50 transition-colors hover:bg-accent/5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <method.icon className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-default font-semibold leading-none text-foreground">
                        {method.title}
                      </h4>
                      <p className="text-sm font-sans font-medium text-foreground">
                        {method.detail}
                      </p>
                      <p className="text-sm font-sans text-muted-foreground">
                        {method.description}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
              <CardFooter className="flex flex-col items-start gap-4 pb-8">
                <div className="bg-muted/50 rounded-lg p-4 w-full border border-border/30">
                  <h4 className="font-default font-semibold text-foreground mb-2 text-sm">Formal Data Request</h4>
                  <p className="text-sm font-sans text-muted-foreground mb-4">
                    To exercise your rights (such as requesting access to or deletion of your data), please use our secure portal. We respond to all verified requests within thirty days.
                  </p>
                  <Button className="w-full sm:w-auto group bg-primary text-primary-foreground hover:bg-primary/90 font-sans" asChild>
                    <Link to="/ContactPage">
                      Submit Secure Request
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}